import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { CurvedTransition, FadeIn, FadeOut } from 'react-native-reanimated';

import { BodyText, Icon, theme } from '@sg/garnish';

import { BUNDLE_TRANSITION_DURATION } from '../../Bundle.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleSlot = (props: Props) => {
  const { label, disabled, onPress } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, disabled ? styles.containerDisabled : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View entering={SLOT_ENTERING} exiting={SLOT_EXITING} layout={SLOT_LAYOUT}>
      <TouchableOpacity
        accessibilityRole="button"
        activeOpacity={0.8}
        disabled={disabled}
        style={containerStyles}
        onPress={onPress}
      >
        <View style={styles.iconContainer}>
          <Icon name="IconPlus" color={theme.colors.DARK_KALE} />
        </View>

        <BodyText sizeMatch={SLOT_LABEL_SIZE_MATCH} style={styles.label}>
          {label}
        </BodyText>
      </TouchableOpacity>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IS_ANIMATED = Platform.OS !== 'web';

const SLOT_ENTERING = IS_ANIMATED ? FadeIn.duration(BUNDLE_TRANSITION_DURATION) : undefined;
const SLOT_EXITING = IS_ANIMATED ? FadeOut.duration(BUNDLE_TRANSITION_DURATION) : undefined;
const SLOT_LAYOUT = IS_ANIMATED ? CurvedTransition.duration(BUNDLE_TRANSITION_DURATION) : undefined;

const SLOT_LABEL_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['18'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
    padding: theme.spacing['2'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  containerDisabled: {
    opacity: 0.5,
  },
  iconContainer: {
    padding: theme.spacing['2'],
    borderRadius: theme.radius.xxlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  label: {
    flex: 1,
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  label: string;
  disabled?: boolean;
  onPress?: () => void;
};
